import { SignInButton, SignedIn, SignedOut, useAuth } from "@clerk/clerk-react";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { resolveClerkPublishableKey } from "../clerk-env";
import {
  beginIdentityVerification,
  completeIdentityVerification,
  formatIdentityError,
} from "../lib/customer-identity-client";
import { fetchCustomerBankProfile } from "../lib/customer-plaid-bank-client";
import { fetchMe, formatMeFetchError } from "../lib/fetch-with-clerk";

type IdentityState = "loading" | "unverified" | "verifying" | "verified" | "failed";

function identityHeading(state: IdentityState): { h1: string; lede: string } {
  switch (state) {
    case "loading":
      return { h1: "Identity verification", lede: "Checking your verification status." };
    case "unverified":
      return {
        h1: "Verify your age",
        lede:
          "Colorado delivery requires proof you are 21+. Verify your ID once for your account with IDScan.net. Separate from bank linking for payment.",
      };
    case "verifying":
      return {
        h1: "Verifying your identity…",
        lede: "We're confirming with IDScan.net. Click \"I finished verification\" once you've completed the steps in the verification window.",
      };
    case "verified":
      return { h1: "You're age-verified", lede: "Verification on file. No action needed." };
    case "failed":
      return {
        h1: "Identity check failed",
        lede: "We couldn't complete verification. Try again or contact support.",
      };
  }
}

function Header({ state }: { state: IdentityState }) {
  const heading = identityHeading(state);
  return (
    <header className="space-y-2 border-b border-[var(--card-border)] pb-6">
      <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--green)]">Account</p>
      <h1 className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">{heading.h1}</h1>
      <p className="text-sm text-[var(--text-secondary)]">{heading.lede}</p>
    </header>
  );
}

export function AccountIdentityScreen() {
  const clerkPk = resolveClerkPublishableKey();
  if (!clerkPk) {
    return (
      <div className="mx-auto w-full max-w-[1200px] space-y-6 pt-6 pb-16">
        <Header state="unverified" />
        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-6 shadow-cardMd">
          <p className="text-sm text-amber-900">
            Set <span className="font-mono">VITE_CLERK_PUBLISHABLE_KEY</span> to verify your age.
          </p>
        </div>
      </div>
    );
  }
  return <AccountIdentityInner />;
}

function AccountIdentityInner() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [meError, setMeError] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [isAgeVerified, setIsAgeVerified] = useState<boolean | null>(null);
  const [ageVerifiedAt, setAgeVerifiedAt] = useState<string | null>(null);
  const [sessionBusy, setSessionBusy] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [activeVerificationId, setActiveVerificationId] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);

  const loadAgeStatus = useCallback(async () => {
    if (!customerId) return;
    const profile = await fetchCustomerBankProfile(getToken, customerId);
    const extended = profile as typeof profile & {
      isAgeVerified?: boolean;
      ageVerifiedAt?: string | null;
    };
    setIsAgeVerified(Boolean(extended.isAgeVerified));
    setAgeVerifiedAt(
      typeof extended.ageVerifiedAt === "string" && extended.ageVerifiedAt.length > 0
        ? extended.ageVerifiedAt
        : null,
    );
  }, [customerId, getToken]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    let cancelled = false;
    setMeError(null);
    void (async () => {
      try {
        const me = await fetchMe(getToken);
        if (cancelled) return;
        if (!me.user.roles.includes("customer") || !me.user.customerId) {
          setMeError(
            !me.user.roles.includes("customer")
              ? `This account does not have the customer role (primary role shown as "${me.user.role}").`
              : "No customer profile id — sign out and back in, or confirm API auto-provisioning.",
          );
          return;
        }
        setCustomerId(me.user.customerId);
      } catch (e) {
        if (!cancelled) setMeError(formatMeFetchError(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, getToken]);

  useEffect(() => {
    if (!customerId || !isSignedIn) return;
    let cancelled = false;
    void (async () => {
      try {
        await loadAgeStatus();
      } catch {
        if (!cancelled) setIsAgeVerified(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId, isSignedIn, loadAgeStatus]);

  const onBegin = useCallback(async () => {
    setSessionError(null);
    setSessionBusy(true);
    try {
      const created = await beginIdentityVerification(getToken);
      setActiveVerificationId(created.applicantId);
      if (created.validationLink != null && created.validationLink.length > 0) {
        window.open(created.validationLink, "_blank", "noopener,noreferrer");
      } else {
        setSessionError("The verification service did not return a link. Try again or contact support.");
      }
    } catch (e) {
      setSessionError(formatIdentityError(e));
    } finally {
      setSessionBusy(false);
    }
  }, [getToken]);

  const onComplete = useCallback(async () => {
    if (!activeVerificationId) {
      setSessionError("Start verification first — use \"Begin verification\" to open the verification window.");
      return;
    }
    setSessionError(null);
    setSessionBusy(true);
    try {
      await completeIdentityVerification(getToken, activeVerificationId);
      setActiveVerificationId(null);
      await loadAgeStatus();
    } catch (e) {
      setSessionError(formatIdentityError(e));
    } finally {
      setSessionBusy(false);
    }
  }, [activeVerificationId, getToken, loadAgeStatus]);

  /** Per redline 08 flag 5: 4-state H1, derived from state machine. */
  let state: IdentityState;
  if (isAgeVerified === null && !sessionBusy && !activeVerificationId) {
    state = "loading";
  } else if (isAgeVerified === true) {
    state = "verified";
  } else if (sessionError != null && !sessionBusy && !activeVerificationId) {
    state = "failed";
  } else if (sessionBusy || activeVerificationId != null) {
    state = "verifying";
  } else {
    state = "unverified";
  }

  return (
    <div className="mx-auto w-full max-w-[1200px] space-y-6 pt-6 pb-16">
      <Header state={state} />

      <SignedOut>
        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-6 text-center shadow-cardMd">
          <p className="text-sm text-[var(--text-secondary)]">Sign in to verify your identity.</p>
          <SignInButton mode="modal">
            <button
              type="button"
              className="mt-4 inline-flex items-center rounded-lg bg-[var(--green)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--green-dark)]"
            >
              Sign in
            </button>
          </SignInButton>
        </div>
      </SignedOut>

      <SignedIn>
        {meError != null ? (
          <div
            role="alert"
            className="rounded-2xl border border-[var(--badge-red)] bg-[var(--status-error-bg)] p-4 text-sm text-[var(--status-error-text)]"
          >
            {meError}
          </div>
        ) : state === "verified" ? (
          <div className="rounded-2xl border border-[var(--green)] bg-[var(--green-pale)] p-5 text-sm text-[var(--green-dark)] shadow-cardMd">
            <p className="font-semibold">Age verified (21+)</p>
            {ageVerifiedAt != null ? (
              <p className="mt-1 text-xs">Confirmed at {new Date(ageVerifiedAt).toLocaleString()}</p>
            ) : null}
          </div>
        ) : state === "verifying" ? (
          <div className="rounded-2xl border border-[var(--card-border)] bg-[var(--surface-sage)] p-5 text-sm text-[var(--green-dark)] shadow-cardMd">
            <p className="font-semibold">Verification in progress</p>
            <p className="mt-1 text-xs">
              Complete the ID capture in the new tab, then click below.
            </p>
            <button
              type="button"
              disabled={sessionBusy}
              onClick={() => void onComplete()}
              className="mt-3 inline-flex items-center rounded-lg bg-[var(--green)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--green-dark)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sessionBusy ? "Confirming…" : "I finished verification"}
            </button>
            {activeVerificationId != null ? (
              <p className="mt-3 text-[10px] text-[var(--text-muted)]">
                Session id: <span className="font-mono">{activeVerificationId}</span>
              </p>
            ) : null}
          </div>
        ) : state === "failed" ? (
          <div className="space-y-3">
            <div
              role="alert"
              className="rounded-2xl border border-[var(--badge-red)] bg-[var(--status-error-bg)] p-4 text-sm text-[var(--status-error-text)]"
            >
              {sessionError ?? "Identity check failed."}
            </div>
            <button
              type="button"
              disabled={sessionBusy}
              onClick={() => {
                setSessionError(null);
                void onBegin();
              }}
              className="inline-flex items-center rounded-lg bg-[var(--green)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--green-dark)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sessionBusy ? "Working…" : "Try again"}
            </button>
          </div>
        ) : state === "unverified" ? (
          <div className="space-y-4 rounded-2xl border border-[var(--card-border)] bg-white p-5 shadow-cardMd">
            <label className="flex cursor-pointer items-start gap-3 text-sm text-[var(--text-secondary)]">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-[var(--card-border)] text-[var(--green)] focus:ring-[var(--green)]"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
              />
              <span>
                I agree that my ID may be shared with IDScan.net for identity verification, as described on
                its verification page.
              </span>
            </label>
            <button
              type="button"
              disabled={!consent || sessionBusy}
              onClick={() => void onBegin()}
              className="inline-flex items-center rounded-lg bg-[var(--green)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--green-dark)] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {sessionBusy ? "Working…" : "Begin verification"}
            </button>
            {sessionError != null ? (
              <p
                role="alert"
                className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900"
              >
                {sessionError}
              </p>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-[var(--text-muted)]">Loading profile…</p>
        )}
      </SignedIn>

      <div className="border-t border-[var(--card-border)] pt-6">
        <Link
          to="/"
          className="text-sm font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--green)]"
        >
          ← Back to marketplace
        </Link>
      </div>
    </div>
  );
}
